import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, X } from "lucide-react";
import Select from "react-select";
import {
  getStatusOptions,
  updateInvoiceStatus,
} from "../../../api/finance/invoices/invoiceRequests";
import { getAllPaymentRequests } from "../../../api/finance/payment/paymentRequests";
import { getAllReceipts } from "../../../api/finance/receipts/receiptRequests";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import customSelectStyles from "../../../components/ui/customSelectStyles";

const fieldSelectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...customSelectStyles.control(provided, state),
    minHeight: "42px",
    borderRadius: "12px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
};

const InvoiceStatusModal = ({ isOpen, onClose, invoice, onSaved }) => {
  const [processingStatus, setProcessingStatus] = useState("");
  const [note, setNote] = useState("");
  const [linkedPaymentId, setLinkedPaymentId] = useState("");
  const [linkedReceiptId, setLinkedReceiptId] = useState("");
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [receiptOptions, setReceiptOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !invoice) return;
    setProcessingStatus(invoice.processing_status || "");
    setNote("");
    setLinkedPaymentId(invoice.linked_payment_id || "");
    setLinkedReceiptId(invoice.linked_receipt_id || "");
  }, [isOpen, invoice]);

  useEffect(() => {
    if (!isOpen) return;
    getAllPaymentRequests()
      .then((items) => {
        const list = Array.isArray(items) ? items : [];
        setPaymentOptions(
          list.slice(0, 100).map((p) => ({
            value: p.id,
            label: `${p.request_code || p.id} — ${Number(p.amount || 0).toLocaleString("vi-VN")} ₫`,
          }))
        );
      })
      .catch(() => setPaymentOptions([]));

    getAllReceipts({ page: 1, page_size: 100 })
      .then((res) => {
        const items = res?.records || [];
        setReceiptOptions(
          items.map((r) => ({
            value: r.id,
            label: `${r.receipt_code} — ${Number(r.amount || 0).toLocaleString("vi-VN")} ₫`,
          }))
        );
      })
      .catch(() => setReceiptOptions([]));
  }, [isOpen]);

  if (!invoice) return null;

  const statusOptions = getStatusOptions(invoice.invoice_type).map((o) => ({
    value: o.value,
    label: o.label,
  }));

  const selectedStatus =
    statusOptions.find((o) => o.value === processingStatus) || null;
  const selectedPayment =
    paymentOptions.find((o) => o.value === linkedPaymentId) || null;
  const selectedReceipt =
    receiptOptions.find((o) => o.value === linkedReceiptId) || null;

  const handleSubmit = async () => {
    if (!processingStatus) {
      return showErrorToast("Vui lòng chọn trạng thái xử lý.");
    }

    try {
      setSaving(true);
      await updateInvoiceStatus(invoice.id, {
        processing_status: processingStatus,
        note: note.trim() || undefined,
        linked_payment_id:
          invoice.invoice_type === "purchase" ? linkedPaymentId || null : undefined,
        linked_receipt_id:
          invoice.invoice_type === "sale" ? linkedReceiptId || null : undefined,
      });
      showSuccessToast("Cập nhật trạng thái thành công.");
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Update invoice status failed:", error);
      showErrorToast(
        error?.response?.data?.message || "Không thể cập nhật trạng thái."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-[61] p-4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-100">
              <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-black text-slate-800">
                    Cập nhật trạng thái
                  </h3>
                  <p className="text-sm text-slate-500 mt-0.5">
                    HĐ {invoice.invoice_number}
                  </p>
                </div>
                <button type="button" onClick={onClose} className="text-slate-400 hover:text-red-600">
                  <X size={20} />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                    Trạng thái xử lý
                  </label>
                  <Select
                    value={selectedStatus}
                    onChange={(opt) => setProcessingStatus(opt?.value || "")}
                    options={statusOptions}
                    styles={fieldSelectStyles}
                    menuPortalTarget={document.body}
                  />
                </div>

                {invoice.invoice_type === "purchase" && (
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                      Liên kết ĐN thanh toán (tùy chọn)
                    </label>
                    <Select
                      value={selectedPayment}
                      onChange={(opt) => setLinkedPaymentId(opt?.value || "")}
                      options={paymentOptions}
                      styles={fieldSelectStyles}
                      menuPortalTarget={document.body}
                      isClearable
                      placeholder="Chọn đề nghị thanh toán..."
                    />
                  </div>
                )}

                {invoice.invoice_type === "sale" && (
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                      Liên kết phiếu thu (tùy chọn)
                    </label>
                    <Select
                      value={selectedReceipt}
                      onChange={(opt) => setLinkedReceiptId(opt?.value || "")}
                      options={receiptOptions}
                      styles={fieldSelectStyles}
                      menuPortalTarget={document.body}
                      isClearable
                      placeholder="Chọn phiếu thu..."
                    />
                  </div>
                )}

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                    Ghi chú
                  </label>
                  <textarea
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:border-red-500 outline-none resize-none"
                    placeholder="Ghi chú khi chuyển trạng thái..."
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-xl font-bold text-slate-500"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  className="px-6 py-2.5 rounded-xl bg-red-600 text-white font-bold flex items-center gap-2 disabled:opacity-70"
                >
                  {saving ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={16} />
                  )}
                  Lưu
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default InvoiceStatusModal;
